import * as changeCase from "change-case";
import { Plus, X } from "lucide-react";
import { useEffect } from "react";
import React from "react";
import { useFormContext } from "react-hook-form";
import { cn, FieldIconButton, ui } from "@patient-watch/ui";
import { normalizeSelectFieldSize, SELECT_FIELD_LABEL_CLASSNAME, SelectSeparator } from "@patient-watch/ui/select";
import { FormFieldLockedControl } from "./components/formFieldLockedControl";
import { FormFieldLabelWithAccessory } from "./components/formFieldLabelRow";
import {
    ADD_OWN_OPTION_VALUE,
    FieldWithOwnOptions,
    type FieldOwnOptions,
    useFieldOwnOptions,
} from "./components/OwnOptionField";
import { OptionalMark } from "./components/FormDisplayContext";
import { getOptionTestId } from "./utils/getTestId";
import { groupOptions } from "./utils/groupOptions";
import { reactNodeToPlainString } from "./utils/reactNodeToPlainString";
import type { LooseRHFField } from "./utils/loose-rhf-field";

export const arrayToOptions = (arr: string[]): OptionT[] => {
    return arr.map((item) => ({
        value: item,
        label: item,
    }));
};

export interface OptionT {
    value: string;
    label: React.ReactNode;
    description?: string;
    searchText?: string;
    /** Shown but not selectable. */
    locked?: boolean;
    icon?: React.ReactNode;
    /** Heading shown above consecutive options that share it. Sections are separated by a rule. */
    group?: string;
}

interface Props {
    name: string;
    description?: string;
    placeholder?: string;
    required?: boolean;
    label?: React.ReactNode;
    disabled?: boolean;
    options: OptionT[];
    side?: "top" | "bottom";
    size?: "sm" | "md" | "lg";
    autoFocus?: boolean;
    clearable?: boolean;
    dependsOn?: string; // Add this prop to specify which field this select depends on
    addOptionButton?: React.ReactNode;
    labelTrailing?: React.ReactNode;
    /** Keep the order of `options` (for example `sort_order` from the database) instead of sorting by label. */
    preserveOrder?: boolean;
    /** `popper` opens under the trigger. `item-aligned` places the selected item over the trigger. */
    position?: "popper" | "item-aligned";
    /** "Add your own option": an "Other" item at the end of the list, and edit and delete for the patient's own. */
    ownOptions?: FieldOwnOptions;
}

const Select = ({
    name,
    description,
    label,
    options,
    placeholder,
    side,
    size,
    autoFocus,
    dependsOn,
    clearable,
    required,
    disabled,
    addOptionButton,
    labelTrailing,
    preserveOrder,
    position,
    ownOptions,
}: Props) => {
    const form = useFormContext();
    const {
        control,
        setValue,
        formState: { errors },
    } = form;
    const own = useFieldOwnOptions({
        ownOptions,
        name,
        value: form.watch(name),
        onChange: (next) => setValue(name, next, { shouldDirty: true, shouldValidate: true }),
    });

    useEffect(() => {
        if (dependsOn && options?.length === 1) {
            // If there's only one option and this field depends on another, set it as default
            setValue(name, options[0]?.value);
        }
    }, [dependsOn, options, setValue, name]);

    const orderedOptions = React.useMemo(() => {
        if (!options) return [];
        const unique = [...new Map(options.map((o) => [o.value, o])).values()];
        if (preserveOrder) return unique;
        return unique.sort((a: OptionT, b: OptionT) =>
            reactNodeToPlainString(a.label).localeCompare(reactNodeToPlainString(b.label))
        );
    }, [options, preserveOrder]);

    const sections = React.useMemo(() => groupOptions<OptionT>(orderedOptions), [orderedOptions]);

    const renderSections = () =>
        sections.map((section, index) => (
            <React.Fragment key={`${section.group ?? "ungrouped"}-${index}`}>
                {index > 0 && <SelectSeparator />}
                <ui.SelectGroup>
                    {section.group && <ui.SelectLabel>{section.group}</ui.SelectLabel>}
                    {section.options.map((option) => (
                        <ui.SelectItem
                            data-testid={getOptionTestId(option)}
                            key={option.value}
                            value={option.value}
                            disabled={option.locked}
                            description={option.description}
                            icon={option.icon}
                        >
                            {option.label}
                        </ui.SelectItem>
                    ))}
                </ui.SelectGroup>
            </React.Fragment>
        ));

    if (!options) {
        return <p className="text-sm">No Options Provided</p>;
    }

    const fieldSize = normalizeSelectFieldSize(size === "lg" ? "md" : size);

    return (
        <ui.FormField
            control={control}
            name={name}
            render={({ field, fieldState }: { field: LooseRHFField; fieldState: { error?: unknown } }) => {
                const fieldError = fieldState.error ?? errors[name];
                return (
                    <ui.FormItem className="w-full">
                        {!!label && (
                            <FormFieldLabelWithAccessory
                                label={
                                    <ui.FormLabel
                                        className={cn(
                                            size === "sm" && "text-xs",
                                            (size === "md" || size == null) && SELECT_FIELD_LABEL_CLASSNAME,
                                            size === "lg" && "text-base"
                                        )}
                                    >
                                        {label}
                                        {!required && <OptionalMark />}
                                    </ui.FormLabel>
                                }
                                labelTrailing={labelTrailing}
                            />
                        )}
                        {!!description && <ui.FormDescription>{description}</ui.FormDescription>}
                        <FormFieldLockedControl>
                            <FieldWithOwnOptions own={own}>
                                <ui.Select
                                    disabled={disabled}
                                    onValueChange={(next: string) => {
                                        if (next === ADD_OWN_OPTION_VALUE) own?.startAdd();
                                        else field.onChange(next);
                                    }}
                                    defaultValue={field.value}
                                    value={field.value}
                                >
                                    <ui.FormControl>
                                        <div className="relative flex-1 min-w-0">
                                            <ui.SelectTrigger
                                                className={cn(
                                                    "w-full",
                                                    clearable &&
                                                        !disabled &&
                                                        field.value != null &&
                                                        field.value !== "" &&
                                                        "[&>span]:pr-6"
                                                )}
                                                testId={`${changeCase.kebabCase(name)}-trigger`}
                                                autoFocus={autoFocus}
                                                size={fieldSize}
                                                aria-invalid={fieldError ? true : undefined}
                                            >
                                                <ui.SelectValue
                                                    placeholder={
                                                        placeholder ?? <span className="text-slate-700">Select</span>
                                                    }
                                                />
                                            </ui.SelectTrigger>
                                            {clearable && !disabled && field.value != null && field.value !== "" && (
                                                <FieldIconButton
                                                    tabIndex={-1}
                                                    aria-label="Clear"
                                                    className="top-1/2 right-8 absolute -translate-y-1/2"
                                                    onPointerDown={(event) => event.preventDefault()}
                                                    onClick={() => field.onChange(null)}
                                                >
                                                    <X aria-hidden />
                                                </FieldIconButton>
                                            )}
                                        </div>
                                    </ui.FormControl>
                                    <ui.SelectContent side={side ?? "bottom"} position={position}>
                                        {orderedOptions.length > 8 ? (
                                            <ui.ScrollArea className="h-[300px]">
                                                {renderSections()}
                                                <ui.ScrollBar orientation="vertical" />
                                            </ui.ScrollArea>
                                        ) : (
                                            renderSections()
                                        )}
                                        {own && !own.atLimit ? (
                                            <>
                                                <SelectSeparator />
                                                <ui.SelectItem
                                                    value={ADD_OWN_OPTION_VALUE}
                                                    data-testid={`form-add-option-${name}`}
                                                    icon={<Plus className="size-4" aria-hidden />}
                                                >
                                                    {own.addLabel}
                                                </ui.SelectItem>
                                            </>
                                        ) : null}
                                    </ui.SelectContent>
                                </ui.Select>
                            </FieldWithOwnOptions>
                        </FormFieldLockedControl>
                        <ui.FormMessage />

                        {addOptionButton}
                    </ui.FormItem>
                );
            }}
        />
    );
};

export default Select;
